/**
 * Программа: карта ступеней, урок навыка и его итог, следующий шаг
 * маршрута мастерства, закрепление и проверка знаний.
 */

import type {
  CompletePractice,
  NextStepView,
  PracticeKind,
  PracticeResult,
  PracticeView,
  CompleteCurriculumLesson,
  CompleteCurriculumLessonResult,
  CurriculumLessonView,
  CurriculumMapView,
  PrefetchNextLessonResult,
  DrillContext,
  DrillHint,
  SimplifiedExplanation,
} from '@aptile/shared';

import { apiRequest, jsonBody } from './client';

export function fetchCurriculumMap(userId: string): Promise<CurriculumMapView> {
  const query = new URLSearchParams({ userId });
  return apiRequest<CurriculumMapView>(`/api/curriculum?${query.toString()}`);
}

/**
 * Срок ответа на запрос урока. Сервер отвечает не позже чем через ~30 с:
 * до 15 с ждёт генерацию и до 15 с перевод, потом отвечает «готовится»
 * (202). Дольше тишины — связь оборвалась, ждать дальше бессмысленно.
 */
export const LESSON_REQUEST_TIMEOUT_MS = 40_000;

export function fetchCurriculumLesson(userId: string, skillId: string, signal?: AbortSignal): Promise<CurriculumLessonView> {
  const query = new URLSearchParams({ userId });
  // `no-store`: пока урок готовится, клиент спрашивает его снова и снова —
  // кэш браузера не должен ни отдавать старый ответ, ни выстраивать
  // одинаковые запросы в очередь друг за другом.
  return apiRequest<CurriculumLessonView>(`/api/curriculum/lessons/${encodeURIComponent(skillId)}?${query.toString()}`, {
    cache: 'no-store',
    signal,
    timeoutMs: LESSON_REQUEST_TIMEOUT_MS,
  });
}

export function completeCurriculumLesson(
  skillId: string,
  input: CompleteCurriculumLesson,
): Promise<CompleteCurriculumLessonResult> {
  return apiRequest<CompleteCurriculumLessonResult>(`/api/curriculum/lessons/${encodeURIComponent(skillId)}/complete`, {
    method: 'POST',
    body: jsonBody(input),
  });
}

/** «Попробовать проще»: объяснение темы урока; на сервере одно на урок. */
export function simplifyLesson(skillId: string): Promise<SimplifiedExplanation> {
  return apiRequest<SimplifiedExplanation>(`/api/lessons/${encodeURIComponent(skillId)}/simplify`, { method: 'POST' });
}

/** «Сомневаюсь»: подсказка к конкретному заданию урока. */
export function fetchDrillHint(skillId: string, drillContext: DrillContext): Promise<DrillHint> {
  return apiRequest<DrillHint>(`/api/lessons/${encodeURIComponent(skillId)}/simplify`, { method: 'POST', body: jsonBody({ drillContext }) });
}

/** Попросить сервер заранее подготовить урок после `skillId` (содержимое не приходит). */
export function prefetchNextLesson(userId: string, skillId: string): Promise<PrefetchNextLessonResult> {
  return apiRequest<PrefetchNextLessonResult>(`/api/curriculum/lessons/${encodeURIComponent(skillId)}/prefetch-next`, {
    method: 'POST',
    body: jsonBody({ userId }),
  });
}

/** Что делать дальше: урок, закрепление, проверка знаний или финиш — и освоенность ступени. */
export function fetchNextStep(): Promise<NextStepView> {
  return apiRequest<NextStepView>('/api/curriculum/next', { cache: 'no-store' });
}

/** Начать закрепление или проверку знаний: сервер подбирает фразы под ученика. */
export function startPractice(kind: PracticeKind): Promise<PracticeView> {
  return apiRequest<PracticeView>('/api/curriculum/practice', { method: 'POST', body: jsonBody({ kind }) });
}

export function completePractice(sessionId: string, input: CompletePractice): Promise<PracticeResult> {
  return apiRequest<PracticeResult>(`/api/curriculum/practice/${encodeURIComponent(sessionId)}/complete`, { method: 'POST', body: jsonBody(input) });
}
